import type { SSOErrorCode, SSOKind } from '@central/api';

export const ssoKindLabel: Record<SSOKind, string> = { oidc: 'OIDC', saml: 'SAML' };

/** Lower-cased email domains from text separated by commas, spaces or new lines. */
export function parseDomains(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((d) => d.trim().replace(/^@/, '').toLowerCase())
    .filter(Boolean);
}

/** The only path an SSO start may leave the console for. */
const startPath = '/api/auth/sso/';

/**
 * The address to open for an SSO start: the server's start URL with the
 * typed email as `email` (an OIDC IdP's login_hint). Anything that is not a
 * same-origin /api/auth/sso/ path is refused (undefined), so a bad discover
 * answer cannot send the browser somewhere else.
 */
export function ssoStartHref(startUrl: string, email = ''): string | undefined {
  let u: URL;
  try {
    u = new URL(startUrl, window.location.origin);
  } catch {
    return undefined;
  }
  if (u.origin !== window.location.origin || !u.pathname.startsWith(startPath)) return undefined;
  if (email) u.searchParams.set('email', email);
  return u.pathname + u.search;
}

/** A full-page navigation: the SSO start sets a cookie and redirects to the IdP. */
export function leaveFor(href: string): void {
  window.location.assign(href);
}

const ssoErrors: Record<SSOErrorCode, string> = {
  sso_expired:
    'That single sign-on attempt expired or was started in another browser. Start it again.',
  no_account:
    'Your identity provider signed you in, but there is no console account for that email. Ask a Rethink Owner to invite you.',
  domain_not_allowed:
    'Your identity provider signed you in with an email outside the domains it covers here. Ask a Rethink Owner to check the setup.',
  sso_failed: 'Single sign-on did not complete. Try again, or sign in with your password.',
  sso_unavailable: 'That single sign-on is no longer available. Sign in with your password.',
  rate_limited: 'Too many sign-in attempts from this network. Wait a minute, then try again.',
};

/** `?ssoError=` as a known code; any other non-empty value reads as sso_failed. */
export function parseSsoError(v: unknown): SSOErrorCode | undefined {
  if (typeof v !== 'string' || !v) return undefined;
  return Object.hasOwn(ssoErrors, v) ? (v as SSOErrorCode) : 'sso_failed';
}

export function ssoErrorMessage(code: SSOErrorCode): string {
  return ssoErrors[code];
}
